// moonowl - main entry (vanilla TS, no framework)

type Variant = 'safe' | 'stretch' | 'wild';
type Accent = 'cyan' | 'magenta' | 'amber';
type BgMode = 'nebula' | 'terminal' | 'chart';
type Density = 'dense' | 'regular' | 'spacious';
type PageName = 'home' | 'services' | 'symphony';
type LogKind = 'info' | 'ok' | 'warn' | 'pay';

interface Tweaks { variant: Variant; accent: Accent; bg: BgMode; density: Density; intensity: number; }
interface EditModeMessage { type: '__activate_edit_mode' | '__deactivate_edit_mode'; }
interface Service { n: string; tag: string; title: string; body: string; }
interface Block { n: string; title: string; outcome: string; involved: string[]; right: string; engagement: string; duration: string; deliverable: string; }
interface Agent { id: string; x: number; label: string; sub: string; }
interface LogStep { agent: 'composer' | 'treasurer' | 'steward' | 'sentinel'; text: string; kind: LogKind; }
interface Shape { top: string; left: string; size: number; kind: 'sphere' | 'cube' | 'octa' | 'tet'; r: number; dur: number; }
interface ChartNode { x: number; y: number; r: number; bright: boolean; }
interface NamedStar { x: number; y: number; name: string; }

const $id = <T extends HTMLElement = HTMLElement>(id: string): T => {
  const el = document.getElementById(id) as T | null;
  if (!el) throw new Error("missing #" + id);
  return el;
};
const $maybe = <T extends HTMLElement = HTMLElement>(id: string): T | null =>
  document.getElementById(id) as T | null;

/* ───── Tweak panel toggle + messaging ───── */
const tweakDefaults: Tweaks = /*EDITMODE-BEGIN*/{
  "variant": "wild",
  "accent": "cyan",
  "bg": "nebula",
  "density": "regular",
  "intensity": 100
}/*EDITMODE-END*/;

let tweaks: Tweaks = Object.assign({}, tweakDefaults);

window.addEventListener('message', (e: MessageEvent<EditModeMessage>) => {
  if (!e.data) return;
  const tweaksEl = $id('tweaks');
  if (e.data.type === '__activate_edit_mode') tweaksEl.classList.add('open');
  if (e.data.type === '__deactivate_edit_mode') tweaksEl.classList.remove('open');
});
window.parent.postMessage({ type: '__edit_mode_available' }, '*');

function applyTweaks(): void {
  document.body.dataset.variant = tweaks.variant;
  document.body.dataset.density = tweaks.density;
  document.body.dataset.bg = tweaks.bg;
  document.documentElement.style.setProperty('--intensity', String(tweaks.intensity / 100));
  const accents: Record<Accent, { v: string; rgb: string }> = {
    cyan: { v: '#7de3ef', rgb: '125,227,239' },
    magenta: { v: '#e074c8', rgb: '224,116,200' },
    amber: { v: '#e5b873', rgb: '229,184,115' },
  };
  document.documentElement.style.setProperty('--accent', accents[tweaks.accent].v);
  document.documentElement.style.setProperty('--accent-rgb', accents[tweaks.accent].rgb);
  $id('int-v').textContent = String(tweaks.intensity);
  $id<HTMLInputElement>('int').value = String(tweaks.intensity);
  chip('chips-variant','variant',tweaks.variant);
  chip('chips-accent','a',tweaks.accent);
  chip('chips-bg','bg',tweaks.bg);
  chip('chips-density','d',tweaks.density);
}

function chip(groupId: string, key: string, value: string): void {
  document.querySelectorAll<HTMLElement>(`#${groupId} .chip`).forEach((c) => {
    c.classList.toggle('on', c.dataset[key] === value);
  });
}

document.querySelectorAll<HTMLElement>('#chips-variant .chip').forEach((c) => { c.onclick = () => { tweaks.variant = c.dataset.variant as Variant; chip('chips-variant','variant',tweaks.variant); applyTweaks(); save(); }; });
document.querySelectorAll<HTMLElement>('#chips-accent .chip').forEach((c) => { c.onclick = () => { tweaks.accent = c.dataset.a as Accent; chip('chips-accent','a',tweaks.accent); applyTweaks(); save(); }; });
document.querySelectorAll<HTMLElement>('#chips-bg .chip').forEach((c) => { c.onclick = () => { tweaks.bg = c.dataset.bg as BgMode; chip('chips-bg','bg',tweaks.bg); applyTweaks(); save(); }; });
document.querySelectorAll<HTMLElement>('#chips-density .chip').forEach((c) => { c.onclick = () => { tweaks.density = c.dataset.d as Density; chip('chips-density','d',tweaks.density); applyTweaks(); save(); }; });
$id<HTMLInputElement>('int').oninput = (e) => { tweaks.intensity = +((e.target as HTMLInputElement).value); applyTweaks(); save(); };

function save(): void {
  window.parent.postMessage({ type: '__edit_mode_set_keys', edits: tweaks }, '*');
}

/* ───── Nav between pages ───── */
const pages: PageName[] = ['home','services','symphony'];
function go(name: PageName): void {
  pages.forEach((p) => $id('page-'+p).classList.toggle('active', p===name));
  document.querySelectorAll<HTMLElement>('nav.primary .lnk').forEach((l) => l.classList.toggle('active', l.dataset.p===name));
  try { localStorage.setItem('moonowl-page', name); } catch(e){}
  window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
}
(window as unknown as { go: typeof go }).go = go;
try { const saved = localStorage.getItem('moonowl-page') as PageName | null; if (saved && pages.includes(saved)) go(saved); } catch(e){}

/* ───── Mobile slide-out menu ───── */
(() => {
  const burger = $maybe<HTMLButtonElement>('burger');
  const menu = $maybe('mobile-menu');
  if (!burger || !menu) return;
  const setOpen = (open: boolean) => {
    document.body.classList.toggle('menu-open', open);
    burger.setAttribute('aria-expanded', String(open));
    menu.setAttribute('aria-hidden', String(!open));
  };
  burger.addEventListener('click', () => setOpen(!document.body.classList.contains('menu-open')));
  menu.querySelectorAll<HTMLElement>('a, [data-close]').forEach((el) => {
    el.addEventListener('click', () => setOpen(false));
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && document.body.classList.contains('menu-open')) setOpen(false);
  });
})();

/* ───── Emblem ticks ───── */
(() => {
  const g = document.getElementById('emblemTicks');
  if (!g) return;
  for (let i = 0; i < 36; i++) {
    const a = (i/36) * Math.PI * 2;
    const x1 = 100 + Math.cos(a) * 82;
    const y1 = 100 + Math.sin(a) * 82;
    const x2 = 100 + Math.cos(a) * (i%3===0 ? 88 : 85);
    const y2 = 100 + Math.sin(a) * (i%3===0 ? 88 : 85);
    const l = document.createElementNS('http://www.w3.org/2000/svg','line');
    l.setAttribute('x1',String(x1)); l.setAttribute('y1',String(y1)); l.setAttribute('x2',String(x2)); l.setAttribute('y2',String(y2));
    l.setAttribute('stroke','rgba(242,236,225,0.25)'); l.setAttribute('stroke-width','0.8');
    g.appendChild(l);
  }
})();

/* ───── Services snapshot cards ───── */
const services: Service[] = [
  { n:'01', tag:'architecture', title:'Agent infrastructure architecture', body:"Defining the topology — what's autonomous, what's human-gated, how agents coordinate, where failures are contained." },
  { n:'02', tag:'payments', title:'Agentic payments · x402 / mpp', body:"Production integrations for companies building pay-per-call APIs and agent marketplaces. Receipts, limits, testnet through launch." },
  { n:'03', tag:'inference', title:'Private local LLMs', body:"End-to-end deployments for teams that can't send data to hosted inference. Model, hardware, stack, ops runbook." },
  { n:'04', tag:'governance', title:'Agent wallet governance', body:"Delegation policies, spending ceilings, audit trails, and cryptographic human-override gates for agents that hold money." },
];
(() => {
  const g = $id('svc-grid');
  services.forEach((s: Service) => {
    const el = document.createElement('div');
    el.className = 'svc panel';
    el.innerHTML = `
      <div class="svc-head"><span class="svc-n">${s.n}</span><span class="svc-tag">${s.tag}</span></div>
      <h3>${s.title}</h3>
      <p>${s.body}</p>
      <a href="#" onclick="go('services');return false;">learn more →</a>`;
    g.appendChild(el);
  });
})();

/* ───── Services page blocks ───── */
const blocks: Block[] = [
  { n:'01', title:'Agent infrastructure architecture',
    outcome:"A defined, documented agent topology for your system. You leave with a blueprint: which agents exist, what authority they hold, how they coordinate, where the human gates sit, and how failures are contained.",
    involved:["Current-state infrastructure audit","Agent responsibility mapping","Delegation and permission model","Failure isolation and rollback design","Architecture document + runbook"],
    right:"You're past the proof-of-concept stage and staring at the question of how to run agents in production without handing them the keys.",
    engagement:'Fixed-scope project', duration:'3–4 weeks', deliverable:'Architecture document + review session' },
  { n:'02', title:'Agentic payments · x402 / mpp',
    outcome:"A working payments layer for your agents — whether you're billing other agents for API access, or your agents are paying for data and compute out in the world. Settlement, receipts, and spending controls included.",
    involved:["Protocol selection (x402 vs MPP vs both)","Wallet architecture and key custody","Spending limit and rate-limit design","Facilitator integration","Testing harness with testnet flows","Production rollout plan"],
    right:"You're building on the agentic commerce stack and need someone who has actually shipped against x402 and MPP, not just read about them.",
    engagement:'Fixed-scope project', duration:'4–6 weeks', deliverable:'Working integration + documentation' },
  { n:'03', title:'Private local LLMs',
    outcome:"Production-grade local inference for teams that can't use hosted models. Model selection, hardware sizing, inference stack, security perimeter, and day-2 operations — all hands-on, no slides.",
    involved:["Use-case analysis and model selection","Hardware sizing and procurement guidance","Inference stack setup (vLLM, TGI, llama.cpp)","Network and access controls","Observability and cost tracking","Handoff runbook for your ops team"],
    right:"You're in healthcare, finance, legal, defense-adjacent, or anywhere the answer to \"can we use OpenAI?\" is \"legal says no.\"",
    engagement:'Fixed-scope project', duration:'6–8 weeks', deliverable:'Running system + ops handoff' },
  { n:'04', title:'Agent wallet governance',
    outcome:"A governance layer for agents that hold and spend money — crypto, fiat, or both. Delegation policies, spending ceilings, audit trails, and cryptographic human-override gates.",
    involved:["Policy design — who can spend what, where, under what conditions","Delegation architecture — key custody, scoped authority","Approval workflow for above-threshold actions","Audit trail and forensics","Incident response for compromised agents"],
    right:"Your agents have wallets, or they're about to, and \"it'll be fine\" has stopped feeling reassuring.",
    engagement:'Fixed-scope project', duration:'3–5 weeks', deliverable:'Policy framework + implemented controls' },
];
(() => {
  const g = $id('svc-blocks');
  blocks.forEach((b: Block) => {
    const el = document.createElement('article');
    el.className = 'svc-block';
    el.innerHTML = `
      <div class="svc-left">
        <div class="svc-big-n">${b.n}</div>
        <h2>${b.title}</h2>
      </div>
      <div class="svc-right">
        <div><p class="field-label">the outcome</p><p class="body-prose" style="max-width:58ch;">${b.outcome}</p></div>
        <div><p class="field-label">what's involved</p><ul class="bullets">${b.involved.map(i=>`<li>${i}</li>`).join('')}</ul></div>
        <div><p class="field-label">when this is right</p><p class="body-prose" style="max-width:58ch;">${b.right}</p></div>
        <div><p class="field-label">format</p><div class="format-row">
          <div><div class="k">engagement</div><div class="v">${b.engagement}</div></div>
          <div><div class="k">duration</div><div class="v">${b.duration}</div></div>
          <div><div class="k">deliverable</div><div class="v">${b.deliverable}</div></div>
        </div></div>
      </div>`;
    g.appendChild(el);
  });
})();

/* ───── Architecture diagram ───── */
(() => {
  const svg = $id('arch');
  const agents: Agent[] = [
    { id:'sentinel',  x:120, label:'SENTINEL',  sub:'perimeter' },
    { id:'steward',   x:360, label:'STEWARD',   sub:'availability' },
    { id:'treasurer', x:600, label:'TREASURER', sub:'payments · x402' },
    { id:'composer',  x:840, label:'COMPOSER',  sub:'orchestration' },
  ];
  let inner = `
    <rect x="200" y="60" width="560" height="60" fill="rgba(224,116,200,0.04)" stroke="rgba(224,116,200,0.4)" stroke-width="1" stroke-dasharray="3 4"/>
    <text x="480" y="82" fill="#e074c8" font-size="10" letter-spacing="3" text-anchor="middle">THE CONDUCTOR · POLICY LAYER</text>
    <text x="480" y="104" fill="rgba(242,236,225,0.6)" font-size="9" letter-spacing="2" text-anchor="middle">who acts · who pays · what requires sign-off</text>
  `;
  agents.forEach(a => {
    inner += `
      <line x1="${a.x}" y1="120" x2="${a.x}" y2="220" stroke="rgba(224,116,200,0.22)" stroke-width="1" stroke-dasharray="2 4"/>
      <g>
        <circle cx="${a.x}" cy="250" r="34" fill="rgba(12,12,22,0.8)" stroke="rgba(125,227,239,0.5)" stroke-width="1"/>
        <circle cx="${a.x}" cy="250" r="6" fill="#7de3ef">
          <animate attributeName="opacity" values="0.4;1;0.4" dur="3s" repeatCount="indefinite"/>
        </circle>
        <text x="${a.x}" y="310" fill="#f2ece1" font-size="11" letter-spacing="3" text-anchor="middle">${a.label}</text>
        <text x="${a.x}" y="328" fill="rgba(242,236,225,0.5)" font-size="9" letter-spacing="2" text-anchor="middle">${a.sub}</text>
      </g>
      <line x1="${a.x}" y1="284" x2="${a.x}" y2="390" stroke="rgba(125,227,239,0.2)" stroke-width="1"/>
    `;
  });
  inner += `
    <line x1="80" y1="390" x2="880" y2="390" stroke="rgba(125,227,239,0.35)" stroke-width="1"/>
    <text x="480" y="412" fill="#7de3ef" font-size="10" letter-spacing="3" text-anchor="middle">THE SCORE · ARCHITECTURAL CONTRACT</text>
    <text x="480" y="432" fill="rgba(242,236,225,0.6)" font-size="9" letter-spacing="2" text-anchor="middle">what each agent can see · do · spend · escalate</text>
  `;
  svg.innerHTML = inner;
})();

/* ───── Live demo ───── */
const SCRIPT: LogStep[] = [
  { agent:'composer',  text:'user intent received: refresh dataset-42',         kind:'info' },
  { agent:'composer',  text:'plan: fetch(remote) → validate → cache',            kind:'info' },
  { agent:'treasurer', text:'price discovery · api.remote.ai',                   kind:'info' },
  { agent:'treasurer', text:'x402 quote received · 0.0042 USDC',                 kind:'info' },
  { agent:'treasurer', text:'policy check · within daily ceiling ($8.40)',       kind:'ok'   },
  { agent:'treasurer', text:'payment authorized · 0.0042 USDC → facilitator',    kind:'pay'  },
  { agent:'composer',  text:'payment receipt 0x9fa…3e1 · fetching',              kind:'info' },
  { agent:'steward',   text:'cache degraded · 71% capacity',                     kind:'warn' },
  { agent:'steward',   text:'scaling +1 replica · region iad',                   kind:'ok'   },
  { agent:'sentinel',  text:'auth anomaly · ip 94.177.* · throttled',            kind:'warn' },
  { agent:'sentinel',  text:'key rotation scheduled · t+47s',                    kind:'info' },
  { agent:'composer',  text:'dataset validated · 1.4MB · committing',            kind:'ok'   },
  { agent:'sentinel',  text:'key rotated · sig verified',                        kind:'ok'   },
  { agent:'composer',  text:'task complete · 4 agents · human sigs required: 0', kind:'ok'   },
];
let idx = 0, paid = 0, uptime = 0, rotate = 12;
const logEl = $id('log');
function ts() {
  const t = new Date();
  return String(t.getHours()).padStart(2,'0')+':'+String(t.getMinutes()).padStart(2,'0')+':'+String(t.getSeconds()).padStart(2,'0');
}
function colorFor(agent: LogStep['agent']): string {
  if (agent==='treasurer') return '#e5b873';
  if (agent==='composer') return '#e074c8';
  return '#7de3ef';
}
function kindColor(k: LogKind): string { return k==='warn'?'#e5b873':k==='pay'?'#e074c8':k==='ok'?'#7de3ef':'rgba(242,236,225,0.8)'; }

setInterval(() => {
  const step = SCRIPT[idx % SCRIPT.length]; idx++;
  const row = document.createElement('div');
  row.className = 'row';
  row.innerHTML = `<span class="ts">${ts()}</span><span class="tag" style="color:${colorFor(step.agent)};">[${step.agent}]</span><span style="color:${kindColor(step.kind)};">${step.text}</span>`;
  logEl.appendChild(row);
  if (logEl.children.length > 40 && logEl.firstChild) logEl.removeChild(logEl.firstChild);
  logEl.scrollTop = logEl.scrollHeight;
  if (step.kind === 'pay') { paid = +(paid + 0.0042).toFixed(4); $id('paid').textContent = paid.toFixed(4); }
  const card = $maybe('a-'+step.agent);
  if (card) { card.classList.add('on'); setTimeout(()=>card.classList.remove('on'), 900); }
}, 1600);

setInterval(() => {
  uptime++;
  $id('uptime').textContent = String(Math.floor(uptime/60)).padStart(2,'0')+':'+String(uptime%60).padStart(2,'0');
  rotate = rotate > 0 ? rotate - 1 : 60;
  $id('rotate').textContent = String(rotate).padStart(2,'0');
}, 1000);

/* ───── Network mesh canvas (polygonal wireframe with glow nodes) ───── */
(() => {
  const c = $id<HTMLCanvasElement>('network');
  const ctx = c.getContext('2d')!;
  let w=0,h=0; let nodes: any[]=[]; const edges: any[]=[]; let raf=0; let running=true;
  function resize(){
    const dpr = Math.min(window.devicePixelRatio||1, 2);
    w = c.clientWidth; h = c.clientHeight;
    c.width = w*dpr; c.height = h*dpr;
    ctx.setTransform(dpr,0,0,dpr,0,0);
    const count = Math.max(26, Math.min(56, Math.round(w*h/28000)));
    nodes = Array.from({length:count}).map(()=>{
      const cyan = Math.random() > 0.35;
      return {
        x: Math.random()*w, y: Math.random()*h,
        vx: (Math.random()-0.5)*0.18,
        vy: (Math.random()-0.5)*0.18,
        r: Math.random()*1.6 + 0.8,
        hue: cyan ? 'cyan' : 'magenta',
        phase: Math.random()*Math.PI*2,
      };
    });
  }
  function draw(t: number): void {
    if (!running) return;
    ctx.clearRect(0,0,w,h);
    // edges: connect pairs within threshold
    const T = Math.min(w,h) * 0.22;
    ctx.lineWidth = 1;
    for (let i=0;i<nodes.length;i++){
      const a = nodes[i];
      for (let j=i+1;j<nodes.length;j++){
        const b = nodes[j];
        const dx = a.x-b.x, dy = a.y-b.y;
        const d = Math.hypot(dx,dy);
        if (d < T){
          const alpha = (1 - d/T) * 0.55;
          // color mix based on both nodes
          const color = (a.hue==='magenta' || b.hue==='magenta')
            ? `rgba(224,116,200,${alpha*0.8})`
            : `rgba(125,227,239,${alpha*0.8})`;
          ctx.strokeStyle = color;
          ctx.beginPath(); ctx.moveTo(a.x,a.y); ctx.lineTo(b.x,b.y); ctx.stroke();
        }
      }
    }
    // nodes with glow
    for (const n of nodes){
      n.x += n.vx; n.y += n.vy;
      if (n.x < 0 || n.x > w) n.vx *= -1;
      if (n.y < 0 || n.y > h) n.vy *= -1;
      const pulse = 0.6 + 0.4*Math.sin(t*0.003375 + n.phase);
      const base = n.hue === 'cyan' ? '125,227,239' : '224,116,200';
      // glow
      const g = ctx.createRadialGradient(n.x, n.y, 0, n.x, n.y, n.r*8);
      g.addColorStop(0, `rgba(${base},${0.85*pulse})`);
      g.addColorStop(0.3, `rgba(${base},${0.25*pulse})`);
      g.addColorStop(1, `rgba(${base},0)`);
      ctx.fillStyle = g;
      ctx.beginPath(); ctx.arc(n.x, n.y, n.r*8, 0, Math.PI*2); ctx.fill();
      // core
      ctx.fillStyle = `rgba(${base},${Math.min(1, 0.9*pulse + 0.15)})`;
      ctx.beginPath(); ctx.arc(n.x, n.y, n.r, 0, Math.PI*2); ctx.fill();
    }
    raf = requestAnimationFrame(draw);
  }
  resize();
  window.addEventListener('resize', resize);
  document.addEventListener('visibilitychange', () => {
    running = !document.hidden;
    if (running) raf = requestAnimationFrame(draw); else cancelAnimationFrame(raf);
  });
  raf = requestAnimationFrame(draw);
})();

/* ───── Starfield canvas ───── */
(() => {
  const c = $id<HTMLCanvasElement>('stars');
  const ctx = c.getContext('2d')!;
  let w=0,h=0; let stars: any[]=[]; let raf=0; let running=true;
  function resize() {
    const dpr = Math.min(window.devicePixelRatio||1, 2);
    w = c.clientWidth; h = c.clientHeight;
    c.width = w*dpr; c.height = h*dpr;
    ctx.setTransform(dpr,0,0,dpr,0,0);
    const count = Math.min(Math.round(w*h/10000), 220);
    stars = Array.from({length:count}).map(()=>({
      x: Math.random()*w, y: Math.random()*h,
      r: Math.random()*1.1 + 0.2,
      a: Math.random()*0.7 + 0.15,
      s: Math.random()*0.6 + 0.2,
    }));
  }
  function draw(t: number): void {
    if (!running) return;
    ctx.clearRect(0,0,w,h);
    for (const st of stars) {
      const tw = 0.65 + 0.35*Math.sin(t*0.0018*st.s + st.x);
      ctx.globalAlpha = st.a * tw;
      ctx.fillStyle = '#f2ece1';
      ctx.beginPath(); ctx.arc(st.x, st.y, st.r, 0, Math.PI*2); ctx.fill();
    }
    ctx.globalAlpha = 1;
    raf = requestAnimationFrame(draw);
  }
  resize();
  window.addEventListener('resize', resize);
  document.addEventListener('visibilitychange', () => {
    running = !document.hidden;
    if (running) raf = requestAnimationFrame(draw); else cancelAnimationFrame(raf);
  });
  raf = requestAnimationFrame(draw);
})();

/* ───── Particles canvas ───── */
(() => {
  const c = $id<HTMLCanvasElement>('particles');
  const ctx = c.getContext('2d')!;
  let w=0,h=0; let ps: any[]=[]; let raf=0; let running=true;
  function resize(){
    const dpr = Math.min(window.devicePixelRatio||1, 2);
    w = c.clientWidth; h = c.clientHeight;
    c.width = w*dpr; c.height = h*dpr;
    ctx.setTransform(dpr,0,0,dpr,0,0);
    ps = Array.from({length:26}).map((_,i)=>({
      x: Math.random()*w, y: Math.random()*h,
      vx: (Math.random()-0.5)*0.27,
      vy: -Math.random()*0.3375 - 0.0675,
      r: Math.random()*1.4 + 0.6,
      c: i%5===0 ? 'rgba(224,116,200,0.55)' : 'rgba(125,227,239,0.5)',
    }));
  }
  function draw(): void {
    if (!running) return;
    ctx.clearRect(0,0,w,h);
    for (const p of ps) {
      p.x += p.vx; p.y += p.vy;
      if (p.y < -10) { p.y = h+10; p.x = Math.random()*w; }
      if (p.x < -10) p.x = w+10; if (p.x > w+10) p.x = -10;
      ctx.fillStyle = p.c;
      ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, Math.PI*2); ctx.fill();
    }
    raf = requestAnimationFrame(draw);
  }
  resize();
  window.addEventListener('resize', resize);
  document.addEventListener('visibilitychange', () => {
    running = !document.hidden;
    if (running) raf = requestAnimationFrame(draw); else cancelAnimationFrame(raf);
  });
  raf = requestAnimationFrame(draw);
})();

/* ───── Wireframes ───── */
(() => {
  const el = $id('wires');
  const shapes: Shape[] = [
    { top:'12%', left:'6%',  size:120, kind:'sphere', r:0,   dur:22 },
    { top:'22%', left:'84%', size:100, kind:'cube',   r:0,   dur:22 },
    { top:'68%', left:'8%',  size:120, kind:'octa',   r:14,  dur:22 },
    { top:'74%', left:'80%', size:80,  kind:'tet',    r:-30, dur:22 },
  ];
  // vertex coords (in 0..100 viewBox) where glowing orbs sit at intercepts
  const verts: Record<Shape['kind'], Array<[number,number]>> = {
    sphere: [
      // 6 axis endpoints (front/back/left/right/top/bottom)
      [50,10],[50,90],[10,50],[90,50],
      // diagonal intercepts on equator ellipse (rotated 30°)
      [85,30],[15,70],
      // center glow
      [50,50],
    ],
    cube: [
      [50,14],[86,32],[86,72],[50,90],[14,72],[14,32],
      [50,54],
    ],
    octa: [[50,8],[90,50],[50,92],[10,50]],
    tet:  [[50,10],[90,82],[10,82]],
  };
  const polygons: Record<Shape['kind'], string> = {
    cube: `
      <path d="M50 14 L86 32 L86 72 L50 90 L14 72 L14 32 Z"/>
      <path d="M50 14 L50 54 L86 72 M50 54 L14 72"/>
      <path d="M14 32 L50 54 L86 32" stroke-dasharray="2 3"/>
    `,
    sphere: `
      <circle cx="50" cy="50" r="40"/>
      <ellipse cx="50" cy="50" rx="40" ry="14"/>
      <ellipse cx="50" cy="50" rx="14" ry="40"/>
      <ellipse cx="50" cy="50" rx="40" ry="28" transform="rotate(30 50 50)"/>
      <path d="M10 50 L90 50 M50 10 L50 90" stroke-dasharray="2 3"/>
    `,
    octa: '<path d="M50 8 L90 50 L50 92 L10 50 Z"/><path d="M10 50 L90 50 M50 8 L50 92"/>',
    tet:  '<path d="M50 10 L90 82 L10 82 Z"/><path d="M50 10 L50 82 M10 82 L90 82"/>',
  };
  shapes.forEach((s: Shape, i: number) => {
    const d = document.createElement('div');
    d.className = 'wire';
    d.style.cssText = `top:${s.top};left:${s.left};width:${s.size}px;height:${s.size}px;--r:${s.r}deg;animation-duration:${s.dur}s;animation-delay:${i*1.2}s;`;
    const filterId = `glow-${i}`;
    // Build vertex orbs: soft glow halo + bright core, with pulsing opacity
    const orbSvg = verts[s.kind].map((v: [number,number], vi: number) => {
      const [vx, vy] = v;
      const dur = (5.5 + (vi % 4) * 1.1).toFixed(2);
      const delay = (vi * 0.9).toFixed(2);
      return `
        <circle cx="${vx}" cy="${vy}" r="3" fill="rgba(125,227,239,0.12)" filter="url(#${filterId})">
          <animate attributeName="r" values="2.4;3.6;2.4" dur="${dur}s" begin="${delay}s" repeatCount="indefinite"/>
          <animate attributeName="opacity" values="0.05;0.45;0.05" dur="${dur}s" begin="${delay}s" repeatCount="indefinite"/>
        </circle>
        <circle cx="${vx}" cy="${vy}" r="1" fill="#c8f5fb">
          <animate attributeName="opacity" values="0.12;0.7;0.12" dur="${dur}s" begin="${delay}s" repeatCount="indefinite"/>
        </circle>
      `;
    }).join('');
    d.innerHTML = `
      <svg viewBox="-6 -6 112 112" fill="none" stroke="rgba(125,227,239,0.6)" stroke-width="0.8" stroke-linejoin="round" stroke-linecap="round" style="width:100%;height:100%;overflow:visible;">
        <defs>
          <filter id="${filterId}" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="1.6" result="blur"/>
            <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
          </filter>
        </defs>
        ${polygons[s.kind]}
        ${orbSvg}
      </svg>
    `;
    el.appendChild(d);
  });
})();

/* ───── Star chart drawing ───── */
(() => {
  const svg = $maybe('chart');
  if (!svg) return;
  // seeded PRNG for stable layout
  let s = 42;
  const r = () => (s = (s * 9301 + 49297) % 233280) / 233280;
  const W = 1600, H = 1000;
  // constellation nodes (forming soft owl/eye shapes loosely)
  const nodes: ChartNode[] = [];
  for (let i = 0; i < 120; i++) {
    nodes.push({ x: r()*W, y: r()*H, r: 0.6 + r()*1.6, bright: r() > 0.85 });
  }
  // a handful of "named" bright stars with labels
  const named: NamedStar[] = [
    { x: 380, y: 260, name: 'sentinel · α' },
    { x: 760, y: 210, name: 'steward · β' },
    { x: 1120, y: 320, name: 'treasurer · γ' },
    { x: 1340, y: 640, name: 'composer · δ' },
    { x: 640, y: 760, name: 'conductor · ε' },
  ];
  // connection lines between named stars (constellation)
  const lines: Array<[number, number]> = [[0,1],[1,2],[2,3],[3,4],[4,0],[1,4]];

  let inner = '';
  // background tick grid (sparse)
  for (let i = 0; i < 10; i++) {
    const y = (i/10)*H;
    inner += `<line x1="0" y1="${y}" x2="${W}" y2="${y}" stroke="rgba(242,236,225,0.05)" stroke-width="0.5"/>`;
  }
  for (let i = 0; i < 16; i++) {
    const x = (i/16)*W;
    inner += `<line x1="${x}" y1="0" x2="${x}" y2="${H}" stroke="rgba(242,236,225,0.05)" stroke-width="0.5"/>`;
  }
  // ecliptic (curved dashed arc)
  inner += `<path d="M 0 620 Q 800 460 1600 600" fill="none" stroke="rgba(var(--accent-rgb),0.25)" stroke-width="0.8" stroke-dasharray="4 6"/>`;
  inner += `<text x="40" y="650" font-family="var(--font-mono)" font-size="10" letter-spacing="3" fill="rgba(var(--accent-rgb),0.55)">ECLIPTIC · MMXXVI</text>`;

  // constellation lines
  lines.forEach(([a,b]: [number, number]) => {
    inner += `<line x1="${named[a].x}" y1="${named[a].y}" x2="${named[b].x}" y2="${named[b].y}" stroke="rgba(var(--accent-rgb),0.35)" stroke-width="0.8"/>`;
  });

  // stars
  nodes.forEach((n: ChartNode) => {
    const op = n.bright ? 0.9 : 0.35 + r()*0.3;
    inner += `<circle cx="${n.x.toFixed(1)}" cy="${n.y.toFixed(1)}" r="${n.r.toFixed(2)}" fill="#f2ece1" opacity="${op.toFixed(2)}"/>`;
  });
  // named bright stars w/ halo + label
  named.forEach((n: NamedStar) => {
    inner += `
      <circle cx="${n.x}" cy="${n.y}" r="9" fill="rgba(var(--accent-rgb),0.08)"/>
      <circle cx="${n.x}" cy="${n.y}" r="4" fill="none" stroke="rgba(var(--accent-rgb),0.8)" stroke-width="1"/>
      <circle cx="${n.x}" cy="${n.y}" r="1.8" fill="#f2ece1"/>
      <text x="${n.x+12}" y="${n.y+4}" font-family="var(--font-mono)" font-size="10" letter-spacing="2" fill="rgba(242,236,225,0.7)">${n.name}</text>
    `;
  });
  // corner crosshair markings
  const corners: Array<[number, number]> = [[40,40],[W-40,40],[40,H-40],[W-40,H-40]];
  corners.forEach(([x,y]: [number, number]) => {
    inner += `<g stroke="rgba(var(--accent-rgb),0.5)" stroke-width="0.8" fill="none">
      <line x1="${x-10}" y1="${y}" x2="${x+10}" y2="${y}"/>
      <line x1="${x}" y1="${y-10}" x2="${x}" y2="${y+10}"/>
      <circle cx="${x}" cy="${y}" r="3"/>
    </g>`;
  });
  inner += `<text x="40" y="30" font-family="var(--font-mono)" font-size="10" letter-spacing="3" fill="rgba(242,236,225,0.4)">CHART · 37.58°N 122.36°W</text>`;
  inner += `<text x="${W-40}" y="30" text-anchor="end" font-family="var(--font-mono)" font-size="10" letter-spacing="3" fill="rgba(242,236,225,0.4)">RA 08h · DEC +42°</text>`;
  svg.innerHTML = inner;
})();

applyTweaks();